// LOGOMAN PRO — Firestore-backed public logo gallery.
//
// Schema: a single `logos` collection. Each doc stores all the design fields
// from MyTemplate (so it can be loaded straight back into the editor) plus
// metadata: userId / userName / visibility / createdAt.
//
// Reads:
//   - Anyone (signed in or not) can read docs where visibility === "public".
//     Used by the home page polaroid scatter as a community gallery.
//   - A signed-in user can also read their own private docs (server enforced
//     in firestore.rules).
//
// Writes:
//   - Signed-in users only. Only public saves get persisted to Firestore here;
//     private saves stay in the user's localStorage (cheaper, faster, no row
//     for someone else's quota).

import {
  collection, doc, addDoc, query, where, orderBy, limit, getDocs,
  serverTimestamp, deleteDoc,
} from "firebase/firestore";
import { db, auth } from "./firebase";
import type { MyTemplate } from "../state/logoStore";

const LOGOS = collection(db, "logos");

/** Document shape stored in Firestore. */
export interface PublicLogoDoc extends MyTemplate {
  userId: string;
  userName: string;
  visibility: "public" | "private";
  /** serverTimestamp() at create time. Read back as Firestore Timestamp. */
  createdAt: unknown;
}

/** A row returned to the UI: the design + the doc id and human-friendly date. */
export interface PublicLogoRow extends MyTemplate {
  /** Firestore doc id (different from the local MyTemplate.id). */
  docId: string;
  userName: string;
  visibility: "public" | "private";
}

/**
 * Persist a logo to Firestore as a public, browseable design.
 * Caller MUST be signed in — throws otherwise.
 * Returns the new Firestore doc id so the local copy can store a backref.
 */
export async function publishPublicLogo(template: MyTemplate): Promise<string> {
  const user = auth.currentUser;
  if (!user) throw new Error("Sign in to publish to the public gallery.");

  const payload: Omit<PublicLogoDoc, "createdAt"> & { createdAt: ReturnType<typeof serverTimestamp> } = {
    ...template,
    userId: user.uid,
    userName: user.displayName || user.email?.split("@")[0] || "Anonymous",
    visibility: "public",
    createdAt: serverTimestamp(),
  };

  const ref = await addDoc(LOGOS, payload);
  return ref.id;
}

/**
 * Fetch the most-recent N public logos for the home page gallery.
 * Returns an empty array on any failure (network, rules, Firestore disabled)
 * so the home page falls back to its empty-state UI gracefully.
 */
export async function fetchRecentPublicLogos(maxResults = 24): Promise<PublicLogoRow[]> {
  try {
    const q = query(
      LOGOS,
      where("visibility", "==", "public"),
      orderBy("createdAt", "desc"),
      limit(maxResults),
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => {
      const data = d.data() as PublicLogoDoc;
      return { ...data, docId: d.id };
    });
  } catch (err) {
    // Most common cause: Firestore not enabled in the project, or rules deny reads.
    // Don't blow up the home page — the polaroid scatter has a sensible fallback.
    console.warn("[publicLogos] fetchRecentPublicLogos failed:", err);
    return [];
  }
}

/** Owner-only delete. Caller must be the doc's userId. */
export async function unpublishPublicLogo(docId: string): Promise<void> {
  await deleteDoc(doc(db, "logos", docId));
}
